import { ContentContainer } from "@twinkltech/curriculum-architect-component-library";
import {
  ApiErrorResponse,
  type GenericSubject,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import {
  Button,
  IconActionsExitToApp,
  IconAlertsCheckCircle,
  Text,
} from "@twinkltech/web-ui";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { GenericError } from "@/components/GenericError/GenericError";
import { SubjectCard } from "@/components/SubjectCard/SubjectCard";
import { SUBJECT_CARD_NS } from "@/components/SubjectCard/SubjectCard.i18n";
import { captureAppError } from "@/utils/monitoring/sentry";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import { SubjectHelper } from "@/utils/SubjectHelper/SubjectHelper";

import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import IconImage from "./assets/image.png";


export const ExploreSubjectListContents = ({
  activeSubjectList,
  addOnSubjectList,
  comingSoonSubjectList,
  error,
  isLoading,
}: {
  activeSubjectList: GenericSubject[];
  addOnSubjectList: GenericSubject[];
  comingSoonSubjectList: GenericSubject[];
  error: ApiErrorResponse | null;
  isLoading: boolean;
}) => {
  const { t } = useTranslation(SUBJECT_CARD_NS);
  const { incrementEngagementCount } = useFeedback();

  const { click } = useTwinklAnalytics();
  const purchasedItemsTitleId = useId();

  if (isLoading) {
    return <LoadingPage />;
  } else if (error) {
    captureAppError(error, {
      action: "load",
      area: "explore-subjects",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <div className="flex flex-col max-w-[1200px]">
        {/* Subject Details */}
        <div className="flex gap-6 mb-10" role="region">
          <div className="max-w-[800px] flex flex-col gap-2">
            <Text as="h1" variant="4xl-bold">
              <span className="line-clamp-2">Explore Curriculum Schemes</span>
            </Text>
          </div>
        </div>

        <div aria-labelledby={purchasedItemsTitleId} role="banner">
          {activeSubjectList.length ? (
            <>
              <Text as="h2" id={purchasedItemsTitleId} variant="xl-medium">
                <span className="mt-12">Purchased Schemes</span>
              </Text>
              <Text as="p" className="mb-4">
                Design your sequences in the &apos;My Curricula&apos; section on
                the left for any purchased or available schemes in your current
                subscription.
              </Text>
              <ul
                className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2
            xl:grid-cols-3 gap-4"
              >
                {activeSubjectList.map((subject) => (
                  <li key={subject.slug}>
                    <Link
                      aria-label={`Explore content for ${subject.label}`}
                      className="flex flex-col rounded-lg group/card-link transition-colors"
                      to={generatePath(
                        paths.EXPLORE_SUBJECT_CONTENTS_SCHEME_PATH,
                        {
                          subject_id: subject.id,
                        }
                      )}
                      onClick={() => {
                        incrementEngagementCount();
                        click("click_cta", {
                          ctaCopy: "Explore Content",
                        });
                      }}
                    >
                      <div className="h-[140px] w-full flex items-center justify-center border-b-8 border-b-blue-500 rounded-t-lg bg-blue-50 group-hover/card-link:bg-blue-150 group-focus-within/card-link:bg-blue-150">
                        <img
                          alt=""
                          className="h-[80px] w-[80px] object-contain"
                          src={
                            SubjectHelper.getCardDecorations(subject.slug)
                              .imageUrl
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-5 p-6 bg-white rounded-b-lg border border-t-0 border-gray-200 group-hover/card-link:bg-neutral-100 group-focus-within/card-link:bg-neutral-100">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-100 items-top h-[60px] line-clamp-2 ellipsis">
                            <IconAlertsCheckCircle className="fill-green-500 min-w-[24px] mt-[2px]" />
                            <Text
                              as="h2"
                              className="line-clamp-2 ellipsis"
                              variant="xl-medium"
                            >
                              {subject.label}
                            </Text>
                          </div>
                          <Text
                            as="p"
                            className="text-gray-500 h-[3.3rem] ellipsis line-clamp-2"
                          >
                            {SubjectHelper.getDescription({
                              slug: subject.slug,
                            })}
                          </Text>
                        </div>
                        <div>
                          <Button
                            className="group-hover/card-link:bg-blue-700 group-focus-within/card-link:bg-blue-700 hover:bg-blue-700"
                            size="sm"
                            tabIndex={-1}
                            variant="secondary"
                          >
                            Explore Content
                          </Button>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div>
              <div className="flex flex-col border border-neutural-150 rounded-lg p-10 gap-2 items-center text-center">
                <div className="flex justify-center">
                  <img className="max-w-[174px]" src={IconImage} />
                </div>
                <Text as="h2" id={purchasedItemsTitleId} variant="xl-medium">
                  No Schemes included with your Membership
                </Text>
                <Text className="max-w-[648px]" variant="base-normal">
                  You can still explore the content for all schemes to see
                  what’s available and visit Twinkl to enquire about purchasing
                  Add-Ons individually or upgrading your membership.
                </Text>
                <div>
                  <Button
                    as="a"
                    className="mt-6"
                    href="https://www.twinkl.co.uk/resources/planit-primary-teaching-resources"
                    rel="noopener noreferrer"
                    size="md"
                    target="_blank"
                    variant="outlined"
                    onClick={() =>
                      click("click_cta", { copyCta: "Upgrade or buy add-ons" })
                    }
                  >
                    Find Out More <IconActionsExitToApp />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {addOnSubjectList.length > 0 && (
          <div aria-labelledby="add-on-subject-title" role="region">
            <Text
              as="h2"
              className="mt-12"
              id="add-on-subject-title"
              variant="xl-medium"
            >
              {activeSubjectList.length > 0
                ? t("add-on.title")
                : "Explore all Schemes"}
            </Text>
            <Text as="p" className="mb-4">
              {activeSubjectList.length > 0
                ? t("add-on.description")
                : "Explore the content for additional schemes and find out more about access for your school."}
            </Text>
            <ul className="flex flex-col gap-3">
              {addOnSubjectList.map((subject) => (
                <li key={subject.slug}>
                  <SubjectCard
                    subject={subject}
                    to={generatePath(
                      paths.EXPLORE_SUBJECT_CONTENTS_SCHEME_PATH,
                      {
                        subject_id: subject.id,
                      }
                    )}
                    variant="add-on"
                    onClick={() => incrementEngagementCount()}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        <div aria-labelledby="coming-soon-title" role="region">
          <Text
            as="h2"
            className="mt-12"
            id="coming-soon-title"
            variant="xl-medium"
          >
            {t("in-development.title")}
          </Text>
          <Text as="p" className="mb-4">
            {t("in-development.description")}
          </Text>
          <ul className="flex flex-col gap-3">
            {comingSoonSubjectList.map((subject) => (
              <li key={subject.slug}>
                <SubjectCard
                  subject={subject}
                  target="_blank"
                  to={`${
                    paths.external.TWINKL_URL
                  }${SubjectHelper.getExploreLink({
                    slug: subject.slug,
                  })}`}
                  variant="in-development"
                  // onClick={() =>
                  //   click("click_cta", { copyCta: "Find out more at Twinkl" })
                  // }
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ContentContainer>
  );
};
